/**
 * AIChatWidget — Floating AI assistant chat widget available on every page.
 *
 * - Fixed bottom-right button with AI sparkle icon
 * - Expands into a panel with chat + conversation history
 * - Shares the same backend as /dashboard/ai
 * - Remembers open/closed state in sessionStorage
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAccessToken } from '../services/api';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  modelUsed?: string;
}

interface Conversation {
  id: string;
  title: string;
  lastMessageAt: string;
  messageCount: number;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function AIChatWidget() {
  const { user } = useAuth();
  const companyId = user?.companyId;

  const [open, setOpen] = useState(() => sessionStorage.getItem('ai-widget-open') === 'true');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(() => sessionStorage.getItem('ai-active-conversation') || null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

  // Persist open state
  useEffect(() => {
    sessionStorage.setItem('ai-widget-open', String(open));
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  // Persist active conversation ID (shared with full AI page)
  useEffect(() => {
    if (conversationId) sessionStorage.setItem('ai-active-conversation', conversationId);
    else sessionStorage.removeItem('ai-active-conversation');
  }, [conversationId]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch conversations when history panel opens
  useEffect(() => {
    if (!showHistory || !companyId) return;
    const fetchConvs = async () => {
      const token = getAccessToken();
      if (!token) return;
      try {
        const res = await fetch(`${baseUrl}/companies/${companyId}/ai/conversations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const r = await res.json();
          if (r.success && r.data) setConversations(r.data);
        }
      } catch { /* ignore */ }
    };
    fetchConvs();
  }, [showHistory, companyId, baseUrl]);

  const handleSend = useCallback(async () => {
    const query = input.trim();
    if (!query || sending || !companyId) return;

    let convId = conversationId;
    if (!convId) {
      convId = crypto.randomUUID();
      setConversationId(convId);
    }

    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', content: query, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const token = getAccessToken();
      const res = await fetch(`${baseUrl}/companies/${companyId}/ai/query`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, conversationId: convId }),
      });
      if (res.ok) {
        const r = await res.json();
        if (r.success && r.data) {
          setMessages(prev => [...prev, {
            id: `a-${Date.now()}`, role: 'assistant', content: r.data.response, timestamp: new Date(), modelUsed: r.data.modelUsed,
          }]);
        }
      } else {
        setMessages(prev => [...prev, { id: `e-${Date.now()}`, role: 'assistant', content: 'Failed to get a response. Try again.', timestamp: new Date() }]);
      }
    } catch {
      setMessages(prev => [...prev, { id: `e-${Date.now()}`, role: 'assistant', content: 'Network error. Please try again.', timestamp: new Date() }]);
    }
    setSending(false);
  }, [input, sending, companyId, conversationId, baseUrl]);

  const loadConversation = async (conv: Conversation) => {
    const token = getAccessToken();
    if (!token || !companyId) return;
    try {
      const res = await fetch(`${baseUrl}/companies/${companyId}/ai/conversations/${conv.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const r = await res.json();
        if (r.success && r.data) {
          const loaded: ChatMessage[] = r.data.map((m: any) => ({
            id: m.id, role: m.role, content: m.content, timestamp: new Date(m.createdAt), modelUsed: m.modelUsed,
          }));
          setMessages(loaded);
          setConversationId(conv.id);
          setShowHistory(false);
        }
      }
    } catch { /* ignore */ }
  };

  const startNew = () => {
    setMessages([]);
    setConversationId(null);
    setShowHistory(false);
  };

  if (!companyId) return null;

  return (
    <>
      <style>{`
        @keyframes aiWidgetSlideUp { from { opacity:0; transform:translateY(16px) scale(.95); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes aiWidgetPulse { 0%,100%{box-shadow:0 0 0 0 rgba(99,102,241,.4)} 50%{box-shadow:0 0 0 8px rgba(99,102,241,0)} }
        .ai-widget-btn:hover { transform: scale(1.08); }
        .ai-widget-msg:last-child { margin-bottom: 0; }
      `}</style>

      {/* Floating Button */}
      {!open && (
        <button
          className="ai-widget-btn"
          onClick={() => setOpen(true)}
          aria-label="Open AI Assistant"
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
            width: 52, height: 52, borderRadius: '50%',
            background: 'var(--accent)', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(99,102,241,.35)',
            transition: 'transform .15s',
            animation: 'aiWidgetPulse 3s ease-in-out infinite',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          width: 380, height: 520, maxHeight: 'calc(100vh - 100px)',
          borderRadius: 16, overflow: 'hidden',
          background: 'var(--bg-1)', border: '1px solid var(--line)',
          boxShadow: '0 8px 40px rgba(0,0,0,.25)',
          display: 'flex', flexDirection: 'column',
          animation: 'aiWidgetSlideUp .2s ease-out',
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid var(--line)',
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--bg-2)',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--fg-0)' }}>AI Assistant</span>
            <button onClick={() => setShowHistory(!showHistory)} title="Conversations" style={{ all: 'unset', cursor: 'pointer', padding: 4, borderRadius: 6, color: showHistory ? 'var(--accent)' : 'var(--fg-3)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </button>
            <button onClick={startNew} title="New conversation" style={{ all: 'unset', cursor: 'pointer', padding: 4, borderRadius: 6, color: 'var(--fg-3)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
            <button onClick={() => setOpen(false)} title="Close" style={{ all: 'unset', cursor: 'pointer', padding: 4, borderRadius: 6, color: 'var(--fg-3)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          {/* History Panel */}
          {showHistory ? (
            <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
              {conversations.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--fg-4)', fontSize: 12, padding: 24 }}>No conversations yet</div>
              ) : conversations.map(conv => (
                <button key={conv.id} onClick={() => loadConversation(conv)} style={{
                  all: 'unset', display: 'block', width: '100%', cursor: 'pointer',
                  padding: '10px 12px', borderRadius: 8, marginBottom: 4,
                  background: conversationId === conv.id ? 'var(--accent-soft)' : 'transparent',
                  transition: 'background .1s',
                }}
                  onMouseEnter={e => { if (conversationId !== conv.id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-2)'; }}
                  onMouseLeave={e => { if (conversationId !== conv.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <div style={{ fontSize: 12, color: conversationId === conv.id ? 'var(--accent)' : 'var(--fg-1)', fontWeight: conversationId === conv.id ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {conv.title}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--fg-4)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                    {new Date(conv.lastMessageAt).toLocaleDateString()} · {conv.messageCount} msgs
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <>
              {/* Messages */}
              <div style={{ flex: 1, overflow: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {messages.length === 0 && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--fg-4)' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: .6 }}>
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    <span style={{ fontSize: 12 }}>Ask about your infrastructure</span>
                  </div>
                )}
                {messages.map(msg => (
                  <div key={msg.id} className="ai-widget-msg" style={{
                    display: 'flex', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row', gap: 8,
                  }}>
                    <div style={{
                      maxWidth: '85%', padding: '8px 12px', borderRadius: 10,
                      fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      background: msg.role === 'user' ? 'var(--accent)' : 'var(--bg-2)',
                      color: msg.role === 'user' ? '#fff' : 'var(--fg-1)',
                      border: msg.role === 'user' ? 'none' : '1px solid var(--line)',
                    }}>
                      {msg.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div style={{ display: 'flex', gap: 4, padding: '8px 12px' }}>
                    {[0,1,2].map(i => (
                      <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', opacity: .6, animation: `aiWidgetPulse 1.2s ease-in-out ${i * .2}s infinite` }} />
                    ))}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div style={{ padding: '10px 12px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Ask about your servers..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  disabled={sending}
                  maxLength={2000}
                  style={{
                    flex: 1, border: '1px solid var(--line)', borderRadius: 8,
                    padding: '8px 12px', fontSize: 12.5, color: 'var(--fg-0)',
                    background: 'var(--bg-2)', outline: 'none', fontFamily: 'inherit',
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || sending}
                  style={{
                    width: 32, height: 32, borderRadius: 8, border: 'none',
                    background: 'var(--accent)', color: '#fff', cursor: !input.trim() || sending ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: !input.trim() || sending ? .5 : 1,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              </div>

              {/* Footer link to full page */}
              <a href="/dashboard/ai" style={{ display: 'block', textAlign: 'center', padding: '6px 0', fontSize: 10, color: 'var(--fg-4)', textDecoration: 'none', borderTop: '1px solid var(--line)' }}>
                Open full AI Assistant →
              </a>
            </>
          )}
        </div>
      )}
    </>
  );
}
